import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { useCrudState } from "../hooks/useCrudState";
import DataTable from "./DataTable";
import EntityForm from "./EntityForm";
import type { CrudColumn, CrudField, CrudRow } from "../types";

// ---------------------------------------------------------------------------
// Reusable scaffold every Nuvex Access "section" composes. A section supplies
// its OWN title / columns / fields / seed rows (that's the bespoke, per-entity
// unit); this component provides the shared header + search + table + add/edit/
// delete modal plumbing (same idea as how existing sections reuse Modal/Card).
//
// `readOnly` renders a list/log without create/edit/delete (for the `L`/`V`
// items in the plan).
// ---------------------------------------------------------------------------

interface CrudSectionProps<T extends CrudRow> {
  title: string;
  subtitle?: string;
  icon?: string; // remixicon
  columns: CrudColumn<T>[];
  fields: CrudField[];
  seed: T[];
  /** Field used by the search box (defaults to the first column). */
  searchKey?: keyof T & string;
  readOnly?: boolean;
  addLabel?: string;
}

export default function CrudSection<T extends CrudRow>({
  title,
  subtitle,
  icon,
  columns,
  fields,
  seed,
  searchKey,
  readOnly = false,
  addLabel,
}: CrudSectionProps<T>) {
  const { t } = useTranslation("common");
  const themeClasses = buildThemeClasses();
  const { rows, add, update, remove } = useCrudState<T>(seed);

  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; row?: T }>(null);

  const key = searchKey ?? (columns[0]?.key as keyof T & string);
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => String(r[key] ?? "").toLowerCase().includes(q));
  }, [rows, query, key]);

  const closeModal = () => setModal(null);

  const handleSubmit = (values: Record<string, string>) => {
    if (modal?.mode === "edit" && modal.row) {
      update(modal.row.id, values as Partial<T>);
    } else {
      add(values as unknown as Omit<T, "id">);
    }
    closeModal();
  };

  const actions = readOnly
    ? undefined
    : (row: T) => (
        <div className="flex items-center justify-center gap-1">
          <button
            title="Editar"
            onClick={() => setModal({ mode: "edit", row })}
            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
          >
            <i className="ri-edit-line" />
          </button>
          <button
            title="Eliminar"
            onClick={() => remove(row.id)}
            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      );

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                <i className={`${icon} text-xl`} />
              </span>
            )}
            <div>
              <h1 className={`text-xl font-bold ${themeClasses.text}`}>{title}</h1>
              {subtitle && (
                <p className={`text-sm ${themeClasses.textSecondary}`}>{subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg}`}
            >
              <i className={`ri-search-line ${themeClasses.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("nuvexAccess.search")}
                className={`bg-transparent text-sm outline-none ${themeClasses.text}`}
              />
            </div>
            {!readOnly && (
              <button
                onClick={() => setModal({ mode: "add" })}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
              >
                <i className="ri-add-line" />
                <span className="whitespace-nowrap">
                  {addLabel ?? t("nuvexAccess.add")}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className={`rounded-2xl border p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <DataTable
            rows={filtered}
            columns={columns}
            renderActions={actions}
            emptyText={t("nuvexAccess.empty")}
          />
        </div>

        <p className={`mt-3 text-xs ${themeClasses.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t("nuvexAccess.mockNotice")}
        </p>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <Modal
          title={
            modal.mode === "edit"
              ? `${t("nuvexAccess.edit")} — ${title}`
              : `${t("nuvexAccess.new")} — ${title}`
          }
          onClose={closeModal}
          themeClasses={themeClasses}
          content={
            <EntityForm
              fields={fields}
              initial={modal.row}
              themeClasses={themeClasses}
              submitLabel={modal.mode === "edit" ? t("nuvexAccess.save") : t("nuvexAccess.create")}
              onCancel={closeModal}
              onSubmit={handleSubmit}
            />
          }
        />
      )}
    </div>
  );
}
